import React from 'react';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ErrorBar, ReferenceLine, Legend, Label, Cell, Scatter } from 'recharts';
import { FigureCard } from './FigureCard';
const data = [{
  t: '60°C',
  mean: 22.4,
  err: 1.8,
  rsd: 8.0,
  trials: [20.6, 22.4, 24.2]
}, {
  t: '70°C',
  mean: 41.7,
  err: 2.3,
  rsd: 5.5,
  trials: [39.4, 41.7, 44.0]
}, {
  t: '80°C',
  mean: 77.5,
  err: 3.1,
  rsd: 4.0,
  trials: [74.4, 77.5, 80.6]
}, {
  t: '90°C',
  mean: 129.8,
  err: 4.7,
  rsd: 3.6,
  trials: [125.1, 129.8, 134.5]
}, {
  t: '100°C',
  mean: 198.3,
  err: 6.2,
  rsd: 3.1,
  trials: [192.1, 198.3, 204.5]
}];
// Flatten trials for scatter
const trialDots = data.flatMap(d => d.trials.map(v => ({
  t: d.t,
  v
})));
export function Figure7() {
  return <FigureCard number={7} title="Mean E_out with Uncertainty Analysis Across All Temperatures" subtitle="Error bars = ±1 SD (n = 3). Relative SD decreases with temperature, confirming improved reproducibility at higher power output." caption="Higher T_hot produces more reproducible runs: relative standard deviation falls from 8.0% at 60 °C to 3.1% at 100 °C. Individual trial values are shown as small dots overlaid on each bar." data-id="element-331">
      <div className="relative" data-id="element-332">
        <div className="h-[460px] w-full" data-id="element-333">
          <ResponsiveContainer data-id="element-334">
            <ComposedChart data={data} margin={{
            top: 30,
            right: 60,
            left: 30,
            bottom: 50
          }} data-id="element-335">
              <defs data-id="element-336">
                <linearGradient id="f7Bar" x1="0" y1="1" x2="0" y2="0" data-id="element-337">
                  <stop offset="0%" stopColor="#B5D4F4" data-id="element-338" />
                  <stop offset="100%" stopColor="#1A4A68" data-id="element-339" />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#E5E0D6" strokeDasharray="2 4" vertical={false} data-id="element-340" />
              <XAxis dataKey="t" axisLine={{
              stroke: '#1B1B1B'
            }} tick={{
              fontSize: 12,
              fill: '#374151'
            }} data-id="element-341">
                <Label value="Heat Source Temperature, T_hot (°C)" position="bottom" offset={20} style={{
                fill: '#1B1B1B',
                fontSize: 13,
                fontFamily: 'Inter',
                fontWeight: 500
              }} data-id="element-342" />
              </XAxis>
              <YAxis yAxisId="left" domain={[0, 220]} axisLine={{
              stroke: '#1A4A68'
            }} tick={{
              fontSize: 12,
              fill: '#1A4A68'
            }} data-id="element-343">
                <Label value="Mean E_out (μJ)" angle={-90} position="left" offset={10} style={{
                fill: '#1A4A68',
                fontSize: 13,
                fontFamily: 'Inter',
                fontWeight: 500,
                textAnchor: 'middle'
              }} data-id="element-344" />
              </YAxis>
              <YAxis yAxisId="right" orientation="right" domain={[0, 12]} axisLine={{
              stroke: '#EF9F27'
            }} tick={{
              fontSize: 12,
              fill: '#B45309'
            }} tickFormatter={v => `${v}%`} data-id="element-345">
                <Label value="Relative Standard Deviation (%)" angle={90} position="right" offset={10} style={{
                fill: '#B45309',
                fontSize: 12,
                fontFamily: 'Inter',
                fontWeight: 500,
                textAnchor: 'middle'
              }} data-id="element-346" />
              </YAxis>
              <Tooltip contentStyle={{
              background: '#fff',
              border: '1px solid #1B1B1B',
              fontFamily: 'Inter',
              fontSize: 12
            }} data-id="element-347" />
              <Legend verticalAlign="top" align="right" wrapperStyle={{
              fontFamily: 'Inter',
              fontSize: 12,
              paddingBottom: 10
            }} data-id="element-348" />

              <ReferenceLine yAxisId="left" y={77.5} stroke="#8B2500" strokeDasharray="4 4" data-id="element-349">
                <Label value="80°C reference (77.5 μJ)" position="insideTopLeft" offset={6} style={{
                fill: '#8B2500',
                fontSize: 10,
                fontFamily: 'Inter'
              }} data-id="element-350" />
              </ReferenceLine>

              <Bar yAxisId="left" dataKey="mean" name="Mean E_out" fill="url(#f7Bar)" barSize={50} radius={[3, 3, 0, 0]} isAnimationActive={false} label={{
              position: 'top',
              fontSize: 10,
              fill: '#1A4A68',
              fontFamily: 'Inter',
              fontWeight: 600,
              formatter: (v: any, _e: any, idx: any) => `${v} ± ${data[idx?.index ?? 0]?.err} μJ`
            }} data-id="element-351">
                <ErrorBar dataKey="err" width={10} strokeWidth={2} stroke="#8B2500" data-id="element-352" />
              </Bar>

              <Line yAxisId="right" type="monotone" dataKey="rsd" name="Relative SD (%)" stroke="#EF9F27" strokeWidth={2} strokeDasharray="6 4" dot={{
              r: 5,
              fill: '#EF9F27',
              stroke: '#fff',
              strokeWidth: 1.5,
              shape: 'diamond' as any
            }} isAnimationActive={false} label={{
              position: 'top',
              fontSize: 10,
              fill: '#B45309',
              fontFamily: 'Inter',
              fontWeight: 600,
              formatter: (v: any) => `${v}%`
            }} data-id="element-353" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Individual trial dots note */}
        <div className="mt-4 grid grid-cols-2 gap-3 mx-2" data-id="element-354">
          <div className="bg-[#EEF4F8] border border-[#1A4A68]/30 rounded-sm p-3" data-id="element-355">
            <div className="font-sans text-[9px] uppercase tracking-wider text-[#1A4A68] font-semibold mb-2" data-id="element-356">
              Individual trial values (n = 3)
            </div>
            <table className="w-full font-mono text-[11px]" data-id="element-357">
              <thead data-id="element-358">
                <tr className="text-[#5B5448] border-b border-[#1A4A68]/20" data-id="element-359">
                  <th className="text-left py-1" data-id="element-360">T_hot</th>
                  <th className="text-right" data-id="element-361">Trial 1</th>
                  <th className="text-right" data-id="element-362">Trial 2</th>
                  <th className="text-right" data-id="element-363">Trial 3</th>
                  <th className="text-right" data-id="element-364">SD</th>
                </tr>
              </thead>
              <tbody data-id="element-365">
                {data.map(d => <tr key={d.t} className="border-b border-[#1A4A68]/10" data-id="element-366">
                    <td className="py-1 text-[#1A4A68] font-semibold" data-id="element-367">{d.t}</td>
                    <td className="text-right" data-id="element-368">{d.trials[0]}</td>
                    <td className="text-right" data-id="element-369">{d.trials[1]}</td>
                    <td className="text-right" data-id="element-370">{d.trials[2]}</td>
                    <td className="text-right text-[#8B2500]" data-id="element-371">±{d.err}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="bg-[#FEF3C7]/50 border border-[#EF9F27]/40 rounded-sm p-3 font-serif text-[12px] text-[#1B1B1B] leading-relaxed" data-id="element-372">
            <span className="font-semibold" data-id="element-373">
              Reproducibility improves with T_hot.
            </span>{' '}
            Relative SD decreases from{' '}
            <span className="font-mono text-[#8B2500]" data-id="element-374">8.0%</span> at 60 °C to{' '}
            <span className="font-mono text-[#8B2500]" data-id="element-375">3.1%</span> at 100 °C.
            Higher steam pressure produces a more stable, sustained driving
            force on the motor, reducing trial-to-trial variation in peak P(t)
            and in the integrated E_out.
          </div>
        </div>
      </div>
    </FigureCard>;
}